import React, { useEffect, useState } from 'react'
import { PageHeader } from '@/components/ui/PageHeader'
import {
  EmailTemplate,
  EmailTemplateInput,
  createEmailTemplate,
  deactivateEmailTemplate,
  listEmailTemplates,
  updateEmailTemplate,
} from '@/api/labEmailTemplates'

// Feature #257: simple admin page for managing rows in
// ``lab_email_template``. Per the work item, no role restriction for
// now — any authenticated user can list/create/edit/(soft) delete.
//
// Layout: a left-hand list of templates (active by default, with a
// toggle to include inactive ones) and a right-hand form pane that
// switches between "create new" and "edit existing" modes. Soft-delete
// preserves history on ``tap_email_message.template_key`` — this page
// only ever flips ``is_active``, never hard-deletes.

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-white)',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '8px',
  padding: '20px',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--color-dark-grey)',
  textTransform: 'uppercase',
  marginBottom: '4px',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '6px',
  fontSize: '14px',
  fontFamily: 'var(--font-family)',
}

const primaryBtn: React.CSSProperties = {
  padding: '8px 16px',
  backgroundColor: 'var(--color-magenta)',
  color: 'var(--color-white)',
  border: 'none',
  borderRadius: '6px',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'var(--font-family)',
}

const secondaryBtn: React.CSSProperties = {
  ...primaryBtn,
  backgroundColor: 'var(--color-white)',
  color: 'var(--color-black)',
  border: '1px solid var(--color-light-grey)',
}

const dangerBtn: React.CSSProperties = {
  ...primaryBtn,
  backgroundColor: '#dc3545',
}

const emptyDraft: EmailTemplateInput = {
  key: '',
  name: '',
  subject: '',
  bodyHtml: '',
  availableMergeFields: null,
}

const EmailTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [includeInactive, setIncludeInactive] = useState(false)
  const [selectedId, setSelectedId] = useState<string | 'new' | null>(null)
  const [draft, setDraft] = useState<EmailTemplateInput>(emptyDraft)
  const [saving, setSaving] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  // Bug #265: backend can hand back ``detail`` as a string (HTTPException
  // raise), a list of objects (FastAPI 422 ValidationError), or a nested
  // object (e.g. {"message": ..., "errors": [...]}) depending on which
  // layer rejected the request. Storing anything other than a string
  // into ``errorMsg`` and then rendering it crashes React with error
  // #31 ("Objects are not valid as a React child"), which the user sees
  // as a "Something went wrong" page. This helper normalises whatever
  // shape the backend returned into a single human-readable string.
  const errorMessageFrom = (err: unknown, fallback: string): string => {
    const data = (err as { response?: { data?: unknown } })?.response?.data
    const detail = (data as { detail?: unknown } | undefined)?.detail

    if (typeof detail === 'string') return detail
    if (Array.isArray(detail)) {
      // FastAPI ValidationError shape: [{loc, msg, type}, ...].
      // Format each entry on its own line so the user sees what failed.
      return detail
        .map((d) => {
          if (typeof d === 'string') return d
          const msg = (d as { msg?: string })?.msg
          const loc = (d as { loc?: unknown[] })?.loc
          if (msg && Array.isArray(loc) && loc.length > 0) {
            return `${loc.join('.')}: ${msg}`
          }
          return msg || JSON.stringify(d)
        })
        .join('; ')
    }
    if (detail && typeof detail === 'object') {
      const msg = (detail as { message?: string }).message
      if (msg) return msg
      return JSON.stringify(detail)
    }
    // Fall back to top-level shapes: {"message": "..."} from a custom
    // error handler, or axios's own ``err.message``.
    if (data && typeof data === 'object') {
      const msg = (data as { message?: string }).message
      if (msg) return msg
    }
    const axiosMsg = (err as { message?: string })?.message
    return axiosMsg || fallback
  }

  // ``mergeFieldsRaw`` is the user's raw text input. We accept TWO
  // formats and parse on save:
  //
  //   - JSON array: ``["entity", "event"]`` (parsed via JSON.parse).
  //   - Comma-separated names: ``entity, event`` (split on comma).
  //
  // Bug #265 follow-up: previously we only split on comma, so a user
  // who pasted a JSON array got a one-element list containing the
  // literal ``["entity"]`` text — backend validator then rejected the
  // template with a 400 because the templated body referenced ``entity``
  // which wasn't in the (corrupted) declared list. Storing the raw
  // text rather than the parsed array preserves the user's editing
  // state (trailing commas, in-progress brackets) so the textbox
  // doesn't keep snapping back as they type.
  const [mergeFieldsRaw, setMergeFieldsRaw] = useState('')

  const loadTemplates = async (showInactive: boolean) => {
    setLoading(true)
    try {
      const data = await listEmailTemplates(showInactive)
      setTemplates(data)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadTemplates(includeInactive)
  }, [includeInactive])

  const selectTemplate = (id: string) => {
    const t = templates.find((x) => x.id === id)
    if (!t) return
    setSelectedId(id)
    setErrorMsg(null)
    setDraft({
      key: t.key,
      name: t.name,
      subject: t.subject,
      bodyHtml: t.bodyHtml,
      availableMergeFields: t.availableMergeFields,
    })
    setMergeFieldsRaw((t.availableMergeFields || []).join(', '))
  }

  const startNew = () => {
    setSelectedId('new')
    setErrorMsg(null)
    setDraft(emptyDraft)
    setMergeFieldsRaw('')
  }

  const cancelEdit = () => {
    setSelectedId(null)
    setErrorMsg(null)
    setDraft(emptyDraft)
    setMergeFieldsRaw('')
  }

  const parsedMergeFields = (): string[] | null => {
    const trimmed = mergeFieldsRaw.trim()
    if (!trimmed) return null

    // Bug #265 follow-up: try JSON-array shape first. If the input is
    // valid JSON AND parses to a list of strings, use it as-is. This
    // handles paste-from-docs cases like ``["entity", "event"]``
    // which used to silently corrupt to ``['["entity", "event"]']``
    // under the old comma-split-only path.
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed)
        if (Array.isArray(parsed)) {
          const stringified = parsed
            .map((v) => (typeof v === 'string' ? v.trim() : String(v)))
            .filter((s) => s.length > 0)
          // Only adopt the JSON parse if EVERY element became a
          // non-empty string — otherwise fall through to comma-split
          // so a malformed paste isn't quietly partial-applied.
          if (stringified.length === parsed.length) return stringified
        }
      } catch {
        // Not valid JSON — fall through to comma-split.
      }
    }

    return trimmed
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
  }

  const handleSave = async () => {
    if (!draft.key.trim() || !draft.name.trim() || !draft.subject.trim() || !draft.bodyHtml.trim()) {
      setErrorMsg('Key, Name, Subject and Body HTML are all required.')
      return
    }
    setSaving(true)
    setErrorMsg(null)
    try {
      const payload: EmailTemplateInput = {
        ...draft,
        availableMergeFields: parsedMergeFields(),
      }
      if (selectedId === 'new') {
        const created = await createEmailTemplate(payload)
        setSelectedId(created.id)
      } else if (selectedId) {
        await updateEmailTemplate(selectedId, payload)
      }
      await loadTemplates(includeInactive)
    } catch (err) {
      setErrorMsg(errorMessageFrom(err, 'Failed to save template'))
    } finally {
      setSaving(false)
    }
  }

  const handleDeactivate = async () => {
    if (!selectedId || selectedId === 'new') return
    if (!confirm('Deactivate this template? It will no longer appear in the compose picker. History on previously-sent emails is preserved.')) {
      return
    }
    setSaving(true)
    setErrorMsg(null)
    try {
      await deactivateEmailTemplate(selectedId)
      await loadTemplates(includeInactive)
      cancelEdit()
    } catch (err) {
      setErrorMsg(errorMessageFrom(err, 'Failed to deactivate template'))
    } finally {
      setSaving(false)
    }
  }

  const selectedTemplate =
    selectedId && selectedId !== 'new' ? templates.find((t) => t.id === selectedId) : null

  return (
    <div>
      <PageHeader
        title="Email Templates"
        subtitle="Manage the templates that appear in the compose-email picker"
      />

      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '16px', marginTop: '16px' }}>
        {/* Left: list */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600 }}>Templates</h3>
            <button onClick={startNew} style={primaryBtn}>+ New</button>
          </div>
          <label style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '12px', marginBottom: '12px', color: 'var(--color-dark-grey)' }}>
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            Show inactive
          </label>
          {loading && (
            <div style={{ color: 'var(--color-dark-grey)', fontSize: '13px' }}>Loading…</div>
          )}
          {!loading && templates.length === 0 && (
            <div style={{ color: 'var(--color-dark-grey)', fontSize: '13px' }}>
              No templates yet — click "New" to create one.
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {templates.map((t) => {
              const active = t.id === selectedId
              return (
                <button
                  key={t.id}
                  onClick={() => selectTemplate(t.id)}
                  style={{
                    textAlign: 'left',
                    padding: '8px 10px',
                    border: active ? '1px solid var(--color-magenta)' : '1px solid transparent',
                    borderRadius: '6px',
                    backgroundColor: active ? 'rgba(214, 51, 132, 0.08)' : 'transparent',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family)',
                  }}
                >
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-black)' }}>
                    {t.name}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--color-dark-grey)' }}>
                    {t.key}
                    {!t.isActive && (
                      <span style={{ marginLeft: '6px', color: '#dc3545' }}>(inactive)</span>
                    )}
                  </div>
                </button>
              )
            })}
          </div>
        </div>

        {/* Right: form pane */}
        <div style={cardStyle}>
          {selectedId === null ? (
            <div style={{ color: 'var(--color-dark-grey)', fontSize: '13px' }}>
              Select a template from the left to edit it, or click "New" to create one.
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600 }}>
                  {selectedId === 'new' ? 'New template' : `Edit: ${selectedTemplate?.name || ''}`}
                </h3>
                {selectedTemplate && !selectedTemplate.isActive && (
                  <span style={{ fontSize: '12px', color: '#dc3545', fontWeight: 600 }}>Inactive</span>
                )}
              </div>

              {errorMsg && (
                <div style={{ marginBottom: '12px', padding: '8px 12px', backgroundColor: 'rgba(220, 53, 69, 0.1)', color: '#dc3545', borderRadius: '6px', fontSize: '13px' }}>
                  {errorMsg}
                </div>
              )}

              <div style={{ display: 'grid', gap: '12px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px' }}>
                  <div>
                    <label style={labelStyle}>Key</label>
                    <input
                      type="text"
                      value={draft.key}
                      onChange={(e) => setDraft({ ...draft, key: e.target.value })}
                      placeholder="e.g. tap.book_phone_screen"
                      style={inputStyle}
                      disabled={selectedId !== 'new'}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Name</label>
                    <input
                      type="text"
                      value={draft.name}
                      onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                      placeholder="e.g. Book Phone Screen"
                      style={inputStyle}
                    />
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Subject</label>
                  <input
                    type="text"
                    value={draft.subject}
                    onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                    placeholder="e.g. We'd love to chat!"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Body HTML</label>
                  <textarea
                    value={draft.bodyHtml}
                    onChange={(e) => setDraft({ ...draft, bodyHtml: e.target.value })}
                    rows={14}
                    placeholder="<p>Hi {{applicant_first_name}},</p>…"
                    style={{ ...inputStyle, fontFamily: 'monospace', resize: 'vertical' }}
                  />
                </div>
                <div>
                  <label style={labelStyle}>
                    Available merge fields
                  </label>
                  <input
                    type="text"
                    value={mergeFieldsRaw}
                    onChange={(e) => setMergeFieldsRaw(e.target.value)}
                    placeholder='applicant_first_name, position_title  —  or  —  ["entity", "event"]'
                    style={inputStyle}
                  />
                  <div style={{ fontSize: '11px', color: 'var(--color-dark-grey)', marginTop: '4px' }}>
                    List the <strong>top-level</strong> Jinja variables your template references —
                    e.g. <code>entity</code> for <code>{'{{entity.applicant.first_name}}'}</code>,
                    not the full dotted path. Accepts either a comma-separated list
                    (<code>entity, event</code>) or a JSON array (<code>{'["entity", "event"]'}</code>).
                    Backend rejects the template if you reference a variable that's not declared here.
                    <br /><br />
                    <strong>Compose-modal templates</strong> (TAP recruiter emails) resolve flat keys:
                    applicant_first_name, applicant_last_name, applicant_full_name,
                    applicant_email, position_title, vacancy_code, reference_number, stage,
                    sender_name, sender_email.
                    <br />
                    <strong>Automation-framework templates</strong> (used by graph.mail.send steps) resolve
                    nested keys under: entity, event, steps, lab_setting, secret, user.
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '8px' }}>
                  {selectedId !== 'new' && selectedTemplate?.isActive && (
                    <button onClick={handleDeactivate} disabled={saving} style={dangerBtn}>
                      Deactivate
                    </button>
                  )}
                  <button onClick={cancelEdit} disabled={saving} style={secondaryBtn}>
                    Cancel
                  </button>
                  <button onClick={handleSave} disabled={saving} style={primaryBtn}>
                    {saving ? 'Saving…' : selectedId === 'new' ? 'Create' : 'Save'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default EmailTemplates
